import React, { useState, DragEvent, ChangeEvent, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import styles from '../Upload.module.scss';
import {
  UploadFile,
  fileMatchesPattern,
  promptRenameFile,
  uploadTypeToAllowedExtensions,
} from '../../../components/files/helpers';

interface UploadDragAndDropProps {
  selectedTemplate: any;
  selectedFiles: UploadFile[];
  setSelectedFiles: (newValue: UploadFile[]) => void;
}

export function UploadDragAndDrop(props: UploadDragAndDropProps) {
  const { selectedTemplate, selectedFiles, setSelectedFiles } = props;
  const [uploadedFiles, setUploadedFiles] = useState<UploadFile[]>(selectedFiles);
  const [dragOver, setDragOver] = useState<boolean>(false);
  const allowedExtensions = uploadTypeToAllowedExtensions[selectedTemplate.uploadType];

  useEffect(() => {
    setSelectedFiles(uploadedFiles);
  }, [uploadedFiles]);

  const handleFileUpload = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) processFiles(files);
  };

  const removeFile = (file: UploadFile) => {
    setUploadedFiles((files) => files.filter((f) => f !== file));
  };

  const renameFile = (file: UploadFile) => {
    promptRenameFile(file, selectedTemplate).then((newFile) => {
      setUploadedFiles(uploadedFiles.map((f) => (f === file ? newFile : f)));
    });
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    setDragOver(false);
    if (files) processFiles(files);
  };

  const processFiles = (files: FileList) => {
    const validFiles: UploadFile[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (allowedExtensions.some((ext) => file.name.endsWith(ext))) {
        validFiles.push(file);
      }
    }
    setUploadedFiles((files) => [...files, ...validFiles]);
  };

  return (
    <>
      <div
        className={`${styles.uploadZone}  ${dragOver ? styles.dragOver : ''} `}
        onDrop={handleDrop}
        onDragOver={(event) => {
          event.preventDefault();

          setDragOver(true);
        }}
        onDragLeave={() => {
          setDragOver(false);
        }}
      >
        <div className={styles.description}>Drag & drop files here ({allowedExtensions.join(', ')})</div>

        <input
          type="file"
          multiple
          accept={allowedExtensions
            .map((ext: string) => {
              const parts = ext.split('.');
              return `.${parts[parts.length - 1]}`;
            })
            .join(',')}
          onChange={handleFileUpload}
          style={{ display: 'none' }}
          id="file-upload"
        />
        <label htmlFor="file-upload">
          <div className={styles.uploadButton}>
            <FontAwesomeIcon className={styles.uploadIcon} icon="file-arrow-up" />
            Choose files
          </div>
        </label>

        {uploadedFiles.length > 0 && (
          <div>
            <strong>Uploaded Files:</strong>
            <ul>
              {uploadedFiles.map((file, index) => {
                return fileMatchesPattern(file.name, selectedTemplate) ? (
                  <li key={index}>
                    {file.name}
                    <FontAwesomeIcon
                      className={styles.removeIcon}
                      icon={['fas', 'trash-can']}
                      onClick={() => removeFile(file)}
                    />
                  </li>
                ) : (
                  <li key={index} className={styles.invalidFile}>
                    {file.name}
                    <FontAwesomeIcon
                      className={styles.invalidFileIcon}
                      icon={['fas', 'pencil']}
                      onClick={() => renameFile(file)}
                    />
                    <FontAwesomeIcon
                      className={styles.invalidFileIcon}
                      icon={['fas', 'trash-can']}
                      onClick={() => removeFile(file)}
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        )}
      </div>
    </>
  );
}
